/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import type { DataProfileColumn } from '../../../types';

interface Props {
  columns: DataProfileColumn[];
  title?: string;
}

export default function BoxPlotSummary({ columns, title = 'Numeric Distribution' }: Props) {
  const numeric = columns.filter((c) => c.min != null && c.max != null && c.q25 != null && c.q75 != null && c.median != null);
  if (!numeric.length) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      {numeric.map((col) => {
        const min = col.min!;
        const max = col.max!;
        const range = max - min || 1;
        const q25Pct = ((col.q25! - min) / range) * 100;
        const medPct = ((col.median! - min) / range) * 100;
        const q75Pct = ((col.q75! - min) / range) * 100;
        const iqrWidth = q75Pct - q25Pct;

        return (
          <Box key={col.name} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ width: 100, minWidth: 100, textAlign: 'right', pr: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {col.name}
            </Typography>
            <Tooltip title={`Min: ${min.toFixed(2)}, Q25: ${col.q25!.toFixed(2)}, Med: ${col.median!.toFixed(2)}, Q75: ${col.q75!.toFixed(2)}, Max: ${max.toFixed(2)}`}>
              <Box sx={{ flex: 1, height: 18, position: 'relative', bgcolor: 'action.hover', borderRadius: 1 }}>
                {/* Whisker line (min to max) */}
                <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, bgcolor: 'divider' }} />
                {/* IQR box */}
                <Box sx={{
                  position: 'absolute',
                  top: 1,
                  bottom: 1,
                  left: `${q25Pct}%`,
                  width: `${Math.max(iqrWidth, 2)}%`,
                  bgcolor: 'primary.main',
                  opacity: 0.5,
                  borderRadius: 0.5,
                }} />
                {/* Median line */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${medPct}%`,
                  width: 2,
                  bgcolor: 'error.main',
                }} />
              </Box>
            </Tooltip>
            <Typography variant="caption" sx={{ width: 50, minWidth: 50, textAlign: 'right', pl: 0.5, fontSize: 10 }}>
              μ={col.mean?.toFixed(1)}
            </Typography>
          </Box>
        );
      })}
    </Paper>
  );
}
